/**
 * Platform Admin KPI data (PLATFORM-KPI-DESIGN.md §3, §5).
 * Live today: typing centers, subscription summary (plan/status/period end), storage snapshot, last active.
 * Everything else is sample data from platformKpi.mock.js and every section reports its `source`.
 * Wiring the backend later = replace the sample calls with /api/platform/kpi/* (design §8.1), nothing else.
 */

import ApiService from '../api.service';
import { keysSnakeToCamel } from '../../utils/apiConvert';
import { cumulativeByMonth, lastMonths } from '../../utils/platformKpi';
import { sampleCenterMetrics, sampleGrowth, sampleRevenue } from './platformKpi.mock';

const DAY = 86400000;

const settledData = (result) => (result.status === 'fulfilled' ? keysSnakeToCamel(result.value?.data?.data ?? null) : undefined);
const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);
const byId = (rows, key) => Object.fromEntries(rows.map((r) => [r[key], r]));

const toPlan = (row) => (row ? {
  name: row.currentPlanName || null,
  tier: row.currentTier || null,
  status: row.status || null,
  paid: row.status === 'ACTIVE' && !!row.currentTier && row.currentTier !== 'FREE',
  periodEnd: row.currentPeriodEnd || null,
  // The summary does not carry auto-renew yet; unknown until the KPI API ships.
  autoRenew: null,
} : null);

export const platformKpiService = {
  /**
   * @returns {Promise<{centers: object[], revenue: object, sources: object, storageComputedAt: string|null, generatedAt: Date}>}
   */
  load: async (period, now = new Date()) => {
    const [centersRes, summaryRes, storageRes, lastActiveRes] = await Promise.allSettled([
      ApiService.get('/api/typing-centers'),
      ApiService.get('/api/admin/subscriptions/summary'),
      ApiService.get('/api/storage/typing-centers', { params: { size: 500 } }),
      ApiService.get('/api/activity/last-active', { params: { size: 500 } }),
    ]);
    if (centersRes.status === 'rejected') throw centersRes.reason;

    const tcs = settledData(centersRes) || [];
    const summary = settledData(summaryRes);
    const storage = settledData(storageRes);
    const lastActive = settledData(lastActiveRes);

    const plans = summary ? byId(summary, 'typingCenterId') : null;
    const storageRows = storage !== undefined ? pageContent(storage) : null;
    const storageById = storageRows ? byId(storageRows, 'typingCenterId') : null;
    const lastActiveById = lastActive !== undefined ? byId(pageContent(lastActive), 'id') : null;

    const centers = tcs.map((tc, index) => {
      const sample = sampleCenterMetrics({ id: tc.id, createdAt: tc.createdAt }, period, index, now);
      const { samplePlan, ...metrics } = sample;
      const plan = plans ? toPlan(plans[tc.id]) : samplePlan;
      return {
        ...metrics,
        id: tc.id,
        name: tc.name,
        code: tc.code,
        active: tc.active !== false,
        createdAt: tc.createdAt || metrics.createdAt,
        plan,
        // A sample failed renewal must never land on a center whose real plan is not paid.
        failedRenewal: metrics.failedRenewal && !!plan?.paid,
        storageBytes: storageById ? (storageById[tc.id]?.totalBytes ?? null) : metrics.storageBytes,
        lastActiveAt: lastActiveById ? (lastActiveById[tc.id]?.lastActiveAt ?? null) : metrics.lastBusinessAt,
      };
    });

    const horizon = now.getTime() + 30 * DAY;
    const renewalsDue30 = centers
      .filter((c) => c.plan?.paid && c.plan.periodEnd)
      .filter((c) => {
        const end = new Date(`${c.plan.periodEnd}T00:00:00+04:00`).getTime();
        return end >= now.getTime() - DAY && end <= horizon;
      })
      .map((c) => ({ id: c.id, name: c.name, periodEnd: c.plan.periodEnd, autoRenew: c.plan.autoRenew }))
      .sort((a, b) => a.periodEnd.localeCompare(b.periodEnd));

    const storageComputedAt = storageRows
      ? storageRows.reduce((acc, r) => (r.computedAt && (!acc || r.computedAt > acc) ? r.computedAt : acc), null)
      : null;

    const revenue = { ...sampleRevenue(centers, period, now), renewalsDue30 };

    // Growth: typing centers are live (counted from created dates); the rest is sample
    // until the monthly snapshot API ships (design §8).
    const months = lastMonths(12, now);
    const sample = sampleGrowth(centers, months, revenue);
    const datesKnown = tcs.every((tc) => tc.createdAt);
    const growth = {
      months,
      series: {
        centers: datesKnown
          ? { values: cumulativeByMonth(tcs.map((tc) => tc.createdAt), months), source: 'live' }
          : { values: months.map(() => centers.length), source: 'sample' },
        ...Object.fromEntries(Object.entries(sample).map(([k, values]) => [k, { values, source: 'sample' }])),
      },
    };

    return {
      centers,
      revenue,
      growth,
      sources: {
        centers: 'live',
        plan: plans ? 'live' : 'sample',
        storage: storageById ? 'live' : 'sample',
        lastActive: lastActiveById ? 'live' : 'sample',
        usage: 'sample',
        potential: 'sample',
        staff: 'sample',
        revenue: 'sample',
        value: 'sample',
      },
      storageComputedAt,
      generatedAt: now,
    };
  },
};

export default platformKpiService;
